import axios from "axios";
import { serializeAxiosError } from "./ReducerUtils";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";


export interface ConfigurationState {
    mainUpdateInterval: number;
    alarmUpdateInterval: number;
    unavailabilityUpdateInterval: number;
    alarmSoundAnimationIntervalDurationByms: number;
    alarmSoundCountRefreshIntervalByms: number;
    sizePagePerformanceAnalysis: number;
    sizePageDailyReport: number;
    commandTreeRefreshDelayByMs: number;

    loading: boolean;
    errorMessage: string[] ;
}

const initialState: ConfigurationState = {
    mainUpdateInterval: 60000,
    alarmUpdateInterval: 60000,
    unavailabilityUpdateInterval: 60000,
    alarmSoundAnimationIntervalDurationByms: 15000,
    alarmSoundCountRefreshIntervalByms: 60000,
    sizePagePerformanceAnalysis: 150,
    sizePageDailyReport: 50,
    commandTreeRefreshDelayByMs: 30000,
    loading: false,
    errorMessage: []
};


export const loadUserConfiguration = () => {
    return async function (dispatch: any) {
        await dispatch(fetchMainUpdateIntervalConfig());
        await dispatch(fetchAlarmUpdateIntervalConfig());
        await dispatch(fetchUnavailabilityUpdateIntervalConfig());
        await dispatch(fetchAlarmSoundAnimationIntervalDurationByMsConfig());
        await dispatch(fetchApiAlarmSoundCountRefreshIntervalByMsConfig());
        await dispatch(fetchApiSizePagePerformanceAnalysisConfig());
        await dispatch(fetchApiSizePageDailyReportConfig());
        await dispatch(fetchApiCommandTreeRefreshDelayByMsConfig());
    }
}
const apiConfigurationUrl = 'api/configuration';
const apiMainUpdateIntervalUrl = `${apiConfigurationUrl}/mainUpdateInterval`;
const apiAlarmUpdateIntervalUrl = `${apiConfigurationUrl}/alarmUpdateInterval`;
const apiUnavailabilityUpdateIntervalUrl = `${apiConfigurationUrl}/unavailabilityUpdateInterval`;
const apiAlarmSoundAnimationIntervalDurationByMsUrl = `${apiConfigurationUrl}/alarmSoundAnimationIntervalDurationByms`;
const apiAlarmSoundCountRefreshIntervalByMsUrl = `${apiConfigurationUrl}/alarmSoundCountRefreshIntervalByms`;
const apiSizePagePerformanceAnalysis = `${apiConfigurationUrl}/sizePagePerformanceAnalysis`;
const apiSizePageDailyReport = `${apiConfigurationUrl}/sizePageDailyReport`;
const apiCommandTreeRefreshDelayByMsUrl = `${apiConfigurationUrl}/commandTreeRefreshDelayByms`;

// Actions
const fetchMainUpdateIntervalConfig = createAsyncThunk<any>(
    'configuration/fetch_apiMainUpdateIntervalConfigUrl', 
    () => axios.get(apiMainUpdateIntervalUrl)
    .then(result => result.data)
    .catch(serializeAxiosError)
);

const fetchAlarmUpdateIntervalConfig = createAsyncThunk<any>(
    'configuration/fetch_apiAlarmUpdateIntervalConfigUrl', 
    () => axios.get(apiAlarmUpdateIntervalUrl)
    .then(result => result.data)
    .catch(serializeAxiosError)
);

const fetchUnavailabilityUpdateIntervalConfig = createAsyncThunk<any>(
    'configuration/fetch_apiUnavailabilityUpdateIntervalConfigUrl',
    () => axios.get(apiUnavailabilityUpdateIntervalUrl)
    .then(result => result.data)
    .catch(serializeAxiosError)
);

const fetchAlarmSoundAnimationIntervalDurationByMsConfig = createAsyncThunk<any>(
    'configuration/fetch_apiAlarmSoundAnimationIntervalDurationByMsConfigUrl',
    () => axios.get(apiAlarmSoundAnimationIntervalDurationByMsUrl)
    .then(result => result.data)
    .catch(serializeAxiosError)
);

const fetchApiAlarmSoundCountRefreshIntervalByMsConfig = createAsyncThunk<any>(
    'configuration/fetch_apiAlarmSoundCountRefreshIntervalByMsUrl',
    () => axios.get(apiAlarmSoundCountRefreshIntervalByMsUrl)
    .then(result => result.data)
    .catch(serializeAxiosError)
);

const fetchApiSizePagePerformanceAnalysisConfig = createAsyncThunk<any>(
    'configuration/fetch_apiSizePagePerformanceAnalysis',
    () => axios.get(apiSizePagePerformanceAnalysis)
        .then(result => result.data)
        .catch(serializeAxiosError)
);

const fetchApiSizePageDailyReportConfig = createAsyncThunk<any>(
    'configuration/fetch_apiSizePageDailyReport',
    () => axios.get(apiSizePageDailyReport)
        .then(result => result.data)
        .catch(serializeAxiosError)
);

const fetchApiCommandTreeRefreshDelayByMsConfig = createAsyncThunk<any>(
    'configuration/fetch_apiCommandTreeRefreshDelayByMs',
    () => axios.get(apiCommandTreeRefreshDelayByMsUrl)
        .then(result => result.data)
        .catch(serializeAxiosError)
);

// Reducer  
export const ConfigurationReducer = createSlice({
    name: 'userConfiguration',
    initialState,
    reducers: {
    },
    extraReducers(builder) { 
        // Fetch Main Update Interval
        builder.addCase(fetchMainUpdateIntervalConfig.pending, (state) => {
            state.loading = true;
            state.mainUpdateInterval = 60000;
        });
        builder.addCase(fetchMainUpdateIntervalConfig.fulfilled, (state, action) => {
            state.loading = false;
            if(action.payload.data){
                state.mainUpdateInterval = toMiliseconds(action.payload.data.value);
            }
            else if(action.payload.title){
                state.errorMessage.push(`fetchMainUpdateIntervalConfig: ${action.payload.title}`);
            }
            console.log('fetchMainUpdateIntervalConfig LOADED')
        });
        builder.addCase(fetchMainUpdateIntervalConfig.rejected, (state, action) => {
            state.loading = false;
            state.errorMessage.push(`fetchMainUpdateIntervalConfig: ${action.payload as string}`);
        });
        // Fetch Alarm Update Interval
        builder.addCase(fetchAlarmUpdateIntervalConfig.pending, (state) => {
            state.loading = true;
            state.alarmUpdateInterval = 60000;
        });
        builder.addCase(fetchAlarmUpdateIntervalConfig.fulfilled, (state, action) => {
            state.loading = false;
            if(action.payload.data){
                state.alarmUpdateInterval = toMiliseconds(action.payload.data.value);
            }
            else if(action.payload.title){
                state.errorMessage.push(`fetchAlarmUpdateIntervalConfig: ${action.payload.title}`);
            }
            console.log('fetchAlarmUpdateIntervalConfig LOADED')
        });
        builder.addCase(fetchAlarmUpdateIntervalConfig.rejected, (state, action) => {
            state.loading = false;
            state.errorMessage.push(`fetchAlarmUpdateIntervalConfig: ${action.payload as string}`);
        });
        // Fetch Unavailability Update Interval
        builder.addCase(fetchUnavailabilityUpdateIntervalConfig.pending, (state) => {
            state.loading = true;
            state.unavailabilityUpdateInterval = 60000;
        });
        builder.addCase(fetchUnavailabilityUpdateIntervalConfig.fulfilled, (state, action) => {
            state.loading = false;
            if(action.payload.data){
                state.unavailabilityUpdateInterval = toMiliseconds(action.payload.data.value);
            }
            else if(action.payload.title){
                state.errorMessage.push(`fetchUnavailabilityUpdateIntervalConfig: ${action.payload.title}`);
            }
            console.log('fetchUnavailabilityUpdateIntervalConfig LOADED')
        });
        builder.addCase(fetchUnavailabilityUpdateIntervalConfig.rejected, (state, action) => {
            state.loading = false;
            state.errorMessage.push(`fetchUnavailabilityUpdateIntervalConfig: ${action.payload as string}`);
        });
        //Fetch Alarm Sound Animation Interval Duration By Ms
        builder.addCase(fetchAlarmSoundAnimationIntervalDurationByMsConfig.pending, (state) => {
            state.loading = true;
            state.alarmSoundAnimationIntervalDurationByms = 60000;
        });
        builder.addCase(fetchAlarmSoundAnimationIntervalDurationByMsConfig.fulfilled, (state, action) => {
            state.loading = false;
            if(action.payload.data){
                state.alarmSoundAnimationIntervalDurationByms = action.payload.data.value;
            }
            else if(action.payload.title){
                state.errorMessage.push(`fetchAlarmSoundAnimationIntervalDurationByMsConfig: ${action.payload.title}`);
            }
            console.log('fetchAlarmSoundAnimationIntervalDurationByMsConfig LOADED')
        });
        builder.addCase(fetchAlarmSoundAnimationIntervalDurationByMsConfig.rejected, (state, action) => {
            state.loading = false;
            state.errorMessage.push(`fetchAlarmSoundAnimationIntervalDurationByMsConfig: ${action.payload as string}`);
        });
        // Fetch Alarm Sound Count Refresh Interval
        builder.addCase(fetchApiAlarmSoundCountRefreshIntervalByMsConfig.pending, (state) => {
            state.loading = true;
            state.alarmSoundCountRefreshIntervalByms = 60000;
        });
        builder.addCase(fetchApiAlarmSoundCountRefreshIntervalByMsConfig.fulfilled, (state, action) => {
            state.loading = false;
            if(action.payload.data){
                state.alarmSoundCountRefreshIntervalByms = action.payload.data.value;
            }
            else if(action.payload.title){
                state.errorMessage.push(`fetchApiAlarmSoundCountRefreshIntervalByMsConfig: ${action.payload.title}`);
            }
            console.log('fetchApiAlarmSoundCountRefreshIntervalByMsConfig LOADED')
        });
        builder.addCase(fetchApiAlarmSoundCountRefreshIntervalByMsConfig.rejected, (state, action) => {
            state.loading = false;
            state.errorMessage.push(`fetchApiAlarmSoundCountRefreshIntervalByMsConfig: ${action.payload as string}`);
        });

        builder.addCase(fetchApiSizePagePerformanceAnalysisConfig.pending, (state) => {
            state.loading = true;
            state.sizePagePerformanceAnalysis = 150;
        });
        builder.addCase(fetchApiSizePagePerformanceAnalysisConfig.fulfilled, (state, action) => {
            state.loading = false;
            if(action.payload.data){
                state.sizePagePerformanceAnalysis = action.payload.data.value;
            }
            else if(action.payload.title){
                state.errorMessage.push(`fetchApiSizePagePerformanceAnalysisConfig: ${action.payload.title}`);
            }
            console.log('fetchApiSizePagePerformanceAnalysisConfig LOADED')
        });
        builder.addCase(fetchApiSizePagePerformanceAnalysisConfig.rejected, (state, action) => {
            state.loading = false;
            state.errorMessage.push(`fetchApiSizePagePerformanceAnalysisConfig: ${action.payload as string}`);
        });

        builder.addCase(fetchApiSizePageDailyReportConfig.pending, (state) => {
            state.loading = true;
            state.sizePageDailyReport = 50;
        });
        builder.addCase(fetchApiSizePageDailyReportConfig.fulfilled, (state, action) => {
            state.loading = false;
            if(action.payload.data){
                state.sizePageDailyReport = action.payload.data.value;
            }
            else if(action.payload.title){
                state.errorMessage.push(`fetchApiSizePageDailyReportConfig: ${action.payload.title}`);
            }
            console.log('fetchApiSizePageDailyReportConfig LOADED')
        });
        builder.addCase(fetchApiSizePageDailyReportConfig.rejected, (state, action) => {
            state.loading = false;
            state.errorMessage.push(`fetchApiSizePageDailyReportConfig: ${action.payload as string}`);
        });

        builder.addCase(fetchApiCommandTreeRefreshDelayByMsConfig.pending, (state) => {
            state.loading = true;
            state.commandTreeRefreshDelayByMs = 30000;
        });
        builder.addCase(fetchApiCommandTreeRefreshDelayByMsConfig.fulfilled, (state, action) => {
            state.loading = false;
            if(action.payload.data){
                state.commandTreeRefreshDelayByMs = action.payload.data.value;
            }
            else if(action.payload.title){
                state.errorMessage.push(`fetchApiCommandTreeRefreshDelayByMsConfig: ${action.payload.title}`);
            }
        });
        builder.addCase(fetchApiCommandTreeRefreshDelayByMsConfig.rejected, (state, action) => {
            state.loading = false;
            state.errorMessage.push(`fetchApiCommandTreeRefreshDelayByMsConfig: ${action.payload as string}`);
        });
    }
});


function toMiliseconds(value: any): number {
    let miliseconds = 1000 * +(value);
    if(miliseconds < 10000){
        miliseconds = 10000;
    }
    return miliseconds;
}

export default ConfigurationReducer.reducer;
